<template>
  <component :is="resolvedTag" class="sp-row" :class="classModifiers">
    <slot />
  </component>
</template>

<script setup>
import { computed } from "vue";
import { toBoolean, toCssUnit } from "#ce/utils/props";
import { row as componentProps } from "./props";

const props = defineProps(componentProps);

// `tag` can arrive from stored Page Builder attributes. `sp-*` values
// resolve to the default tag, keeping the slot out of any registered
// wrapper — under the lazy build an async wrapper holds slotted children in
// an empty shadow root until its chunk resolves. Prop validators cannot
// enforce this (they only warn, and only in development builds).
const resolvedTag = computed(() =>
  props.tag?.toLowerCase().startsWith("sp-") ? componentProps.tag.default : props.tag,
);

const rowGap = computed(() => toCssUnit(props.rowgap));
const columnGap = computed(() => toCssUnit(props.columngap));

const classModifiers = computed(() => ({
  "--no-gutters": toBoolean(props.noGutters),
}));
</script>

<style>
:host {
  display: block;
}
:host([height="100%"]) {
  height: 100%;
}
</style>

<style scoped lang="scss">
.sp-row {
  --row-column-gap: calc(v-bind(columnGap) / 2);
  --row-column-gap-offset: calc(var(--row-column-gap) * -1);
  --column-gap-start: var(--row-column-gap);
  --column-gap-end: var(--row-column-gap);
  --row-gap: v-bind(rowGap);

  display: flex;
  flex-wrap: wrap;
  flex: 1 1 auto;
  align-items: v-bind(align);
  align-content: v-bind(alignContent);
  justify-content: v-bind(justify);
  height: v-bind(height);
  margin-left: calc(var(--row-column-gap) * -1);
  margin-right: calc(var(--row-column-gap) * -1);
  row-gap: var(--row-gap);

  &.--no-gutters {
    --row-column-gap: 0;
  }
}
</style>
